import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { doc, setDoc, collection, getDocs, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebase';

/* inserts a calendar bookedDate to a schedule collection, 
returns the bookedDate object  */
export const createBookedDate = createAsyncThunk(
  'calendarBookedDates/createBookedDate',
  async ({ uid, selectedDay, data }) => {
    try {
      const bookedDateRef = doc(db, 'users', uid, 'bookedDates', selectedDay);
      await setDoc(bookedDateRef, data);
      return { uid, selectedDay, data };
    } catch (error) {
      return error;
    }
  }
);

export const manageBookedDate = createAsyncThunk(
  'calendarbookedDates/manageBookedDate',
  async ({ uid, bookedDates }, { rejectWithValue }) => {
    try {
      if (!bookedDates || !Array.isArray(bookedDates)) {
        return rejectWithValue('Invalid bookedDates structure');
      }
      await Promise.all(
        bookedDates.map(async (bookedDate) => {
          const bookedDateRef = doc(db, 'users', uid, 'bookedDates', bookedDate.date);
          const bookedDateDoc = await getDoc(bookedDateRef);
          if (bookedDateDoc.exists()) {
            const existingData = bookedDateDoc.data();
            // Guard against old format where timeSlots was stored as a plain array at root
            const existingSlots = Array.isArray(existingData?.timeSlots) ? existingData.timeSlots : [];
            await updateDoc(bookedDateRef, { timeSlots: [...existingSlots, ...bookedDate.timeSlots] });
          } else {
            await setDoc(bookedDateRef, { timeSlots: bookedDate.timeSlots });
          }
        })
      );
      return { uid, bookedDates };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to fetch bookedDates
export const fetchBookedDates = createAsyncThunk(
  'calendarBookedDates/fetchBookedDates',
  async (uid) => {
    try {
      const bookedDates = {};
      const snap = await getDocs(collection(db, 'users', uid, 'bookedDates'));
      snap.docs.forEach((doc) => {
        const docId = doc.id;
        bookedDates[docId] = { timeSlots: doc.data().timeSlots || [] };
      });
      return { uid, bookedDates };
    } catch (error) {
      console.error('Error fetching bookedDates:', error);
      return error;
    }
  }
);

// Async thunk to delete specific timeSlots from an bookedDate
export const deleteBookedDateTimeSlots = createAsyncThunk(
  'calendarBookedDates/deleteBookedDateTimeSlots',
  async ({ uid, date, timeSlotsToDelete }) => {
    try {
      const bookedDateRef = doc(db, 'users', uid, 'bookedDates', date);
      const bookedDateDoc = await getDoc(bookedDateRef);

      if (bookedDateDoc.exists()) {
        const existingTimeSlots = Array.isArray(bookedDateDoc.data()?.timeSlots)
          ? bookedDateDoc.data().timeSlots
          : [];
        const updatedTimeSlots = existingTimeSlots.filter(
          (slot) => !timeSlotsToDelete.includes(slot)
        );

        // Update the document with the new timeSlots
        await updateDoc(bookedDateRef, { timeSlots: updatedTimeSlots });
        return { uid, date, updatedTimeSlots };
      } else {
        throw new Error('BookedDate document does not exist');
      }
    } catch (error) {
      return error;
    }
  }
);

const bookedDateInitialState = {
  bookedDates: {},
  loading: false
};

export const bookedDateSlice = createSlice({
  name: 'bookedDates',
  initialState: bookedDateInitialState,
  reducers: {
    resetbookedDates(state) {
      state.bookedDates = {};
      state.loading = false;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(createBookedDate.fulfilled, (state, action) => {
        const { selectedDay, data } = action.payload;
        state.bookedDates[selectedDay] = {
          timeSlots: Array.isArray(data?.timeSlots) ? data.timeSlots : []
        };
      })
      .addCase(manageBookedDate.fulfilled, (state, action) => {
        action.payload.bookedDates.forEach((bookedDate) => {
          const dateString = bookedDate.date;
          const existing = state.bookedDates[dateString];
          // Normalize: handle both old plain-array format and current { timeSlots: [] } format
          const existingSlots = Array.isArray(existing?.timeSlots)
            ? existing.timeSlots
            : Array.isArray(existing)
            ? existing
            : [];
          state.bookedDates[dateString] = {
            timeSlots: [...existingSlots, ...(bookedDate.timeSlots || [])]
          };
        });
      })
      .addCase(manageBookedDate.pending, (state) => {
        state.loading = true;
      })
      .addCase(manageBookedDate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      .addCase(fetchBookedDates.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchBookedDates.fulfilled, (state, action) => {
        state.bookedDates = action.payload.bookedDates;
        state.loading = false;
      })
      .addCase(fetchBookedDates.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message; // Assuming action.error contains the error message
      })
      .addCase(deleteBookedDateTimeSlots.fulfilled, (state, action) => {
        const { date, updatedTimeSlots } = action.payload;
        state.bookedDates[date] = { timeSlots: updatedTimeSlots || [] };
      });
  }
});

export const { resetBookedDates } = bookedDateSlice.actions;
export default bookedDateSlice.reducer;
